import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { AmountUtils } from '@shared/utils';
import Arrow from '@assets/icons/select-arrow.svg';

export interface ItemsType {
  value: string;
  label?: number | string;
  leftLabel: string;
}

interface ISelectProps {
  className?: string;
  wrapperClassName?: string;
  items: ItemsType[];
  onChange: (value?: ItemsType) => void;
  value?: string;
  placeholder?: string;
  double?: boolean;
  isLoading?: boolean;
  selectWidth?: string;
}

export function Select<T>(props: ISelectProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const currentLabelRight = useMemo(() => {
    if (props.isLoading) return 'Loading...';
    if (props.items.length === 0) return undefined;
    if (typeof props.items[0] === 'string') return props.value as string;
    return (props.items as unknown as { label: string; value: T }[]).find((x) => x.value === props.value)?.label;
  }, [props.value, props.items, props.isLoading]);

  const currentLabelLeft = useMemo(() => {
    if (props.isLoading) return 'Loading...';
    if (props.items.length === 0) return undefined;
    if (typeof props.items[0] === 'string') return props.value as string;
    return (props.items as { label: string; value: T; leftLabel: string }[]).find((x) => x.value === props.value)?.leftLabel;
  }, [props.value, props.items, props.isLoading]);

  const handleSelect = (selectedValue: ItemsType) => {
    props.onChange(selectedValue);
    setIsOpen(false);
  };

  return (
    <div className={clsx('w-full cursor-pointer', props.wrapperClassName, props.className)} ref={selectRef}>
      <div
        className={clsx('relative block text-sm', isOpen && 'left-[-5%] w-[110%]', isOpen && props.selectWidth)}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div
          className={clsx(
            'flex h-[50px] w-full items-center justify-between rounded border border-[#46413a] bg-transparent',
            isOpen && 'border-none !bg-[#363838]',
          )}
        >
          <span className={clsx('block max-w-[calc(100%-20px)] truncate pl-5 leading-[50px] text-[#d2d2d2] uppercase')}>
            {currentLabelLeft ?? props.placeholder}
          </span>
          {props.double && (
            <span className='mr-9 text-[#d2d2d2]'>
              {typeof currentLabelRight === 'number' ? AmountUtils.transformToAmount(currentLabelRight) : (currentLabelRight ?? props.placeholder)}
            </span>
          )}
          <span className='absolute top-[50%] right-0 -mt-1 table-cell w-6 cursor-pointer pr-1 text-center align-middle'>
            <Arrow className={clsx(isOpen ? 'rotate-180' : '')} />
          </span>
        </div>

        {isOpen && (
          <div className='absolute z-[900] max-h-[200px] w-full cursor-pointer overflow-y-auto bg-[#363838]'>
            <div>
              {props.items?.map((option, i) => (
                <div
                  key={i}
                  className={clsx(
                    'max-w-full overflow-hidden py-1 text-left text-sm leading-15 tracking-wider text-ellipsis whitespace-nowrap uppercase',
                    option.value === props.value ? 'text-white' : 'text-[#929292]',
                  )}
                  onClick={() => handleSelect(option)}
                >
                  <div className='flex !w-full justify-between px-5 hover:bg-[#2c2c2c]'>
                    <span>{option.leftLabel}</span>
                    {props.double ? (typeof option.label === 'number' ? AmountUtils.transformToAmount(option.label) : option.label) : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
